import styled from "@emotion/styled";
import { DateTime } from "luxon";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import {
  AudioRecordingCard,
  AudioRecordingCardBody,
  AudioRecordingCardHeader,
} from "@/pages/audio-recordings/shared/audio-recording-card";
import { MDSText, MDSTextVariant } from "@/mds";
import type {
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";

type Props = {
  recording?: AudioRecordingObservable | null;
  recordingStatus?: AudioRecordingRecordingStatus;
  uploadStatus?: AudioRecordingUploadStatus;
  processingStatus?: AudioRecordingProcessingStatus;
  locallyCreatedAt?: string | null;
  locallyModifiedAt?: string | null;
  title?: string;
};

export const AudioRecordingSyncStatusCard = ({
  recording,
  recordingStatus,
  uploadStatus,
  processingStatus,
  locallyCreatedAt,
  locallyModifiedAt,
  title = "Audio Recording Sync Model Data",
}: Props) => {
  const effectiveRecordingStatus = recording?.recordingStatus ?? recordingStatus ?? "—";
  const effectiveUploadStatus = recording?.uploadStatus ?? uploadStatus ?? "—";
  const effectiveProcessingStatus = recording?.processingStatus ?? processingStatus ?? "—";
  const effectiveCreatedAt = recording?.locallyCreatedAt ?? locallyCreatedAt ?? null;
  const effectiveModifiedAt = recording?.locallyModifiedAt ?? locallyModifiedAt ?? null;

  return (
    <AudioRecordingCard>
      <AudioRecordingCardHeader title={title} />
      <AudioRecordingCardBody>
        <Row>
          <Label variant={MDSTextVariant.TextXs}>Recording</Label>
          <Value variant={MDSTextVariant.TextSm}>{String(effectiveRecordingStatus)}</Value>
        </Row>
        <Row>
          <Label variant={MDSTextVariant.TextXs}>Upload</Label>
          <Value variant={MDSTextVariant.TextSm}>{String(effectiveUploadStatus)}</Value>
        </Row>
        <Row>
          <Label variant={MDSTextVariant.TextXs}>Processing</Label>
          <Value variant={MDSTextVariant.TextSm}>{String(effectiveProcessingStatus)}</Value>
        </Row>
        {effectiveCreatedAt && (
          <Row>
            <Label variant={MDSTextVariant.TextXs}>Locally Created</Label>
            <Value variant={MDSTextVariant.TextSm}>{formatTimestamp(effectiveCreatedAt)}</Value>
          </Row>
        )}
        {effectiveModifiedAt && (
          <Row>
            <Label variant={MDSTextVariant.TextXs}>Locally Modified</Label>
            <Value variant={MDSTextVariant.TextSm}>{formatTimestamp(effectiveModifiedAt)}</Value>
          </Row>
        )}
      </AudioRecordingCardBody>
    </AudioRecordingCard>
  );
};

const formatTimestamp = (value: string) => {
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return value;
  return dt.toLocaleString(DateTime.DATETIME_MED);
};

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Label = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Value = styled(MDSText)`
  font-feature-settings: "tnum" on;
`;
